// src/modules/ambassadors/Ambassadors.tsx - Главная страница модуля амбассадоров

import React, { useState, useEffect } from 'react';
import {
  Button,
  Table,
  Space,
  Tag,
  Dropdown,
  Typography,
  Row,
  Col,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  ExportOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useAmbassadors } from '@/hooks/useAmbassadors';
import { AmbassadorProfile, AmbassadorFilters as Filters } from '@/types/models/ambassador';
import { AmbassadorFilters } from './components/AmbassadorFilters';
import { AmbassadorModal } from './components/AmbassadorModal';
import { AmbassadorDetailModal } from './components/AmbassadorDetailModal';
import { AmbassadorStats } from './components/AmbassadorStats';
import { GiftItemSelectModal } from './components/GiftItemSelectModal';  // ✅ ДОБАВЛЕНО
import {
  getAmbassadorStatusText,
  getAmbassadorStatusColor,
  formatBonusBalance,
  formatPhoneDisplay,
  formatActivationDate,
} from './utils/ambassadorUtils';

const { Title } = Typography;

export const Ambassadors: React.FC = () => {
  // Хук для работы с данными
  const {
    ambassadors,
    ambassadorsData,
    currentAmbassador,
    programStats,
    settings,  // ✅ ДОБАВЛЕНО
    loading,
    ambassadorsLoading,
    statsLoading,
    loadAmbassadors,
    loadAmbassadorDetail,
    createAmbassador,
    updateAmbassador,
    activateAmbassador,
    deactivateAmbassador,
    bulkUpdateStatus,
    adjustBalance,
    loadProgramStats,
    loadSettings,  // ✅ ДОБАВЛЕНО
    updateSettings,  // ✅ ДОБАВЛЕНО
    exportData,
    clearAmbassadorDetail,
    refresh,
  } = useAmbassadors();

  // Состояние UI
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [giftModalVisible, setGiftModalVisible] = useState(false);  // ✅ ДОБАВЛЕНО
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingAmbassador, setEditingAmbassador] = useState<AmbassadorProfile | null>(null);
  
  // ✅ Фильтруем данные от null и без customer
  const validAmbassadors = ambassadors.filter(
    (amb): amb is AmbassadorProfile => 
      amb !== null && 
      amb !== undefined && 
      amb.customer !== null && 
      amb.customer !== undefined
  );

  // Загрузка статистики и настроек при монтировании
  useEffect(() => {
    loadProgramStats();
    loadAmbassadors(); 
    loadSettings();  // ✅ ДОБАВЛЕНО
  }, []);

  // Обработка фильтрации
  const handleFilter = (filters: Filters) => {
    loadAmbassadors({ ...filters, page: 1 });
  };

  // Открытие модального окна создания
  const handleCreate = () => {
    setModalMode('create');
    setEditingAmbassador(null);
    setModalVisible(true);
  };

  // Открытие модального окна редактирования
  const handleEdit = (ambassador: AmbassadorProfile) => {
    setModalMode('edit');
    setEditingAmbassador(ambassador);
    setModalVisible(true);
  };

  // Просмотр деталей амбассадора
  const handleViewDetails = async (ambassador: AmbassadorProfile) => {
    try {
      await loadAmbassadorDetail(ambassador.id);
      setDetailModalVisible(true);
    } catch (error) {
      // Ошибка обрабатывается в хуке
    }
  };

  // Отправка формы создания/редактирования
  const handleModalSubmit = async (data: any) => {
    if (modalMode === 'create') {
      await createAmbassador(data);
    } else if (editingAmbassador) {
      await updateAmbassador(editingAmbassador.id, data);
    }
  };

  // Активация/деактивация
  const handleToggleStatus = async (ambassador: AmbassadorProfile) => {
    try {
      if (ambassador.is_active) {
        await deactivateAmbassador(ambassador.id);
      } else {
        await activateAmbassador(ambassador.id);
      }
    } catch (error) {
      // Ошибка обрабатывается в хуке
    }
  };

  // Массовые действия
  const handleBulkActivate = async () => {
    try {
      await bulkUpdateStatus(selectedRowKeys as number[], true);
      setSelectedRowKeys([]);
    } catch (error) {
      // Ошибка обрабатывается в хуке
    }
  };

  const handleBulkDeactivate = async () => {
    try {
      await bulkUpdateStatus(selectedRowKeys as number[], false);
      setSelectedRowKeys([]);
    } catch (error) {
      // Ошибка обрабатывается в хуке
    }
  };

  // Экспорт данных
  const handleExport = async () => {
    try {
      await exportData();
    } catch (error) {
      // Ошибка обрабатывается в хуке
    }
  };

  // ✅ ДОБАВЛЕНО: Обработчик выбора подарочного набора
  const handleSelectGiftItem = async (itemId: number) => {
    await updateSettings({ welcome_gift_item: itemId });
    setGiftModalVisible(false);
  };

  // Обработка изменения пагинации
  const handleTableChange = (pagination: any) => {
    loadAmbassadors({
      page: pagination.current,
      per_page: pagination.pageSize,
    });
  };

  // Колонки таблицы
  const columns: ColumnsType<AmbassadorProfile> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Амбассадор',
      key: 'customer',
      render: (_, record: AmbassadorProfile) => {
        if (!record || !record.customer) {
          return <div style={{ color: '#ff4d4f' }}>Ошибка данных</div>;
        }
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.customer.name || 'Без имени'}
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              {formatPhoneDisplay(record.customer.phone || '')}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Промокод',
      dataIndex: 'promo_code',
      key: 'promo_code',
      render: (code: string) => (
        <Tag color="blue" style={{ fontSize: 12 }}>
          {code}
        </Tag>
      ),
      width: 130,
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={getAmbassadorStatusColor(isActive)}>
          {getAmbassadorStatusText(isActive)}
        </Tag>
      ),
      width: 100,
      filters: [
        { text: 'Активные', value: true },
        { text: 'Неактивные', value: false },
      ],
    },
    {
      title: 'Клиентов',
      dataIndex: 'total_new_clients',
      key: 'total_new_clients',
      sorter: (a, b) => a.total_new_clients - b.total_new_clients,
      width: 100,
    },
    {
      title: 'Продажи',
      dataIndex: 'total_lifetime_sales',
      key: 'total_lifetime_sales',
      render: (amount: number) => formatBonusBalance(amount),
      sorter: (a, b) => a.total_lifetime_sales - b.total_lifetime_sales,
      width: 120,
    },
    {
      title: 'Баланс',
      dataIndex: 'ambassador_bonus_balance',
      key: 'ambassador_bonus_balance',
      render: (balance: number) => (
        <span style={{ fontWeight: 500, color: balance > 0 ? '#52c41a' : '#8c8c8c' }}>
          {formatBonusBalance(balance)}
        </span>
      ),
      sorter: (a, b) => a.ambassador_bonus_balance - b.ambassador_bonus_balance,
      width: 120,
    },
    {
      title: 'Дата активации',
      dataIndex: 'date_activated',
      key: 'date_activated',
      render: (date: string | null) => formatActivationDate(date),
      width: 130,
    },
    {
      title: 'Действия',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_, record: AmbassadorProfile) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            Просмотр
          </Button>
          
          <Dropdown
            menu={{
              items: [
                {
                  key: 'edit',
                  label: 'Редактировать',
                  icon: <EditOutlined />,
                  onClick: () => handleEdit(record),
                },
                {
                  key: 'toggle',
                  label: record.is_active ? 'Деактивировать' : 'Активировать',
                  icon: record.is_active ? <CloseOutlined /> : <CheckOutlined />,
                  onClick: () => handleToggleStatus(record),
                },
                {
                  key: 'divider-1',
                  type: 'divider',
                },
              ],
            }}
          >
            <Button size="small">
              Еще <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];

  // Конфигурация выбора строк
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  return (
    <div>
      {/* Заголовок */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Амбассадоры
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={refresh}
              loading={ambassadorsLoading}
            >
              Обновить
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
              loading={loading}
            >
              Экспорт
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Добавить амбассадора
            </Button>
          </Space>
        </Col>
      </Row>

      {/* ✅ ИЗМЕНЕНО: Статистика + Подарочный набор */}
      <AmbassadorStats 
        stats={programStats} 
        settings={settings}
        loading={statsLoading}
        onEditGift={() => setGiftModalVisible(true)}
      />

      {/* Фильтры */}
      <AmbassadorFilters onFilter={handleFilter} loading={ambassadorsLoading} />

      {/* Массовые действия */}
      {selectedRowKeys.length > 0 && (
        <Space style={{ marginBottom: 16 }}>
          <span>Выбрано: {selectedRowKeys.length}</span>
          <Button
            size="small"
            icon={<CheckOutlined />}
            onClick={handleBulkActivate}
            loading={loading}
          >
            Активировать
          </Button>
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={handleBulkDeactivate}
            loading={loading}
          >
            Деактивировать
          </Button>
        </Space>
      )}

      {/* Таблица */}
      <Table
        dataSource={validAmbassadors}
        columns={columns}
        rowKey="id"
        loading={ambassadorsLoading}
        rowSelection={rowSelection}
        pagination={{
          current: 1,
          pageSize: 20,
          total: ambassadorsData?.total_count || 0,
          showSizeChanger: true,
          showTotal: (total) => `Всего: ${total}`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />

      {/* Модальное окно создания/редактирования */}
      <AmbassadorModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setEditingAmbassador(null);
        }}
        onSubmit={handleModalSubmit}
        ambassador={editingAmbassador}
        mode={modalMode}
      />

      {/* Модальное окно деталей */}
      <AmbassadorDetailModal
        visible={detailModalVisible}
        onClose={() => {
          setDetailModalVisible(false);
          clearAmbassadorDetail();
        }}
        ambassador={currentAmbassador}
        onBalanceAdjust={adjustBalance}
      />

      {/* ✅ ДОБАВЛЕНО: Модальное окно выбора подарка */}
      <GiftItemSelectModal
        visible={giftModalVisible}
        onClose={() => setGiftModalVisible(false)}
        onSelect={handleSelectGiftItem}
        currentItemId={settings?.welcome_gift_item || null}
      />
    </div>
  );
};